import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  robots?: string;
  canonical?: string;
}

/**
 * Componente para gerenciar meta tags SEO dinâmicas
 * 
 * @example
 * ```tsx
 * <SEOHead
 *   title="Biblioteca de Remédios Homeopáticos - Magistral"
 *   description="Catálogo completo de remédios homeopáticos..."
 *   keywords="arnica, belladonna, remédios homeopáticos"
 * />
 * ```
 */
export function SEOHead({
  title = 'Magistral Homeopatia - Sua Homeopatia na Hora Certa',
  description = 'Primeiro aplicativo completo para pacientes de homeopatia no Brasil. Gestão inteligente de tratamentos familiares e individuais com lembretes, biblioteca de remédios, consultas com homeopata virtual e gamificação. Grátis para começar.',
  keywords = 'app homeopatia, pacientes homeopatia, lembrete medicação homeopática, tratamento homeopático família, medicina integrativa, gestão de medicamentos, app saúde',
  ogImage = '/og-image.png',
  ogType = 'website',
  robots = 'index, follow',
  canonical
}: SEOHeadProps) {
  const location = useLocation();
  const baseUrl = import.meta.env.VITE_PUBLIC_APP_URL || 'https://magistralhomeopatia.com.br';
  const currentUrl = `${baseUrl}${location.pathname}`;
  const canonicalUrl = canonical || currentUrl;

  useEffect(() => {
    // Title
    document.title = title;

    // Meta tags básicas
    updateMetaTag('name', 'description', description);
    updateMetaTag('name', 'keywords', keywords);
    updateMetaTag('name', 'robots', robots);

    // Open Graph
    updateMetaTag('property', 'og:title', title);
    updateMetaTag('property', 'og:description', description);
    updateMetaTag('property', 'og:image', `${baseUrl}${ogImage}`);
    updateMetaTag('property', 'og:url', currentUrl);
    updateMetaTag('property', 'og:type', ogType);
    updateMetaTag('property', 'og:locale', 'pt_BR');
    updateMetaTag('property', 'og:site_name', 'Magistral Homeopatia');

    // Twitter Cards
    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:title', title);
    updateMetaTag('name', 'twitter:description', description);
    updateMetaTag('name', 'twitter:image', `${baseUrl}${ogImage}`);

    // Canonical URL
    updateLinkTag('canonical', canonicalUrl);
  }, [title, description, keywords, ogImage, ogType, robots, currentUrl, canonicalUrl, baseUrl]);

  return null;
}

/**
 * Helper para atualizar ou criar meta tag
 */
function updateMetaTag(attribute: string, key: string, content: string) {
  let element = document.querySelector(`meta[${attribute}="${key}"]`);
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', content);
}

/**
 * Helper para atualizar ou criar link tag
 */
function updateLinkTag(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  
  element.setAttribute('href', href);
}
